import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Users, Wallet, TrendingUp, CheckCircle2, Clock, ChevronRight,
  LogOut, Activity, ShieldCheck, Eye, EyeOff, Search,
} from "lucide-react";
import { API } from "../config/constants";
import axios from "axios";

/**
 * Showcase admin panel — visual replica of live admin for demo / portfolio
 * walkthroughs. Reads a tiny sanitized snapshot of pending withdrawals
 * (names + amounts) and weaves them into a synthetic 600-row transaction feed.
 * NO real-data mutations are performed by this view.
 */

const SHOW_USER = "admin";
const SHOW_PASS = "@Ahmad628213";
const STORAGE_KEY = "showcase_session_v1";

// === codename generator (matches P2P style) ====================================
const ADJ = ["Swift","Royal","Silent","Golden","Crimson","Ace","Lone","Mystic","Phantom","Iron","Storm","Lunar","Solar","Cobalt","Falcon","Tiger","Hawk","Wolf","Eagle","Lion","Knight","Sage","Vortex","Quantum","Cyber","Velvet","Diamond","Pearl","Onyx","Jade","Apex","Nova","Frost","Ember","Shadow","Crystal","Steel","Titan","Astro","Neon"];
const NOUN = ["Trader","Hunter","Scout","Pilot","Ranger","Voyager","Champion","Warrior","Guardian","Maverick","Pioneer","Captain","Navigator","Strider","Vanguard","Sentinel","Specter","Arbiter","Oracle"];
const codenameFromString = (s) => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  const adj = ADJ[h % ADJ.length];
  const noun = NOUN[Math.floor(h / 100) % NOUN.length];
  const suf = (h % 4096).toString(16).toUpperCase().padStart(3, "0");
  return `${adj}_${noun}_${suf}`;
};

// === synthetic name pool =======================================================
const NAME_POOL = [
  "Rakesh Sharma","Mohammed Imran","Aarav Patel","Priya Singh","Sneha Iyer",
  "Vikram Reddy","Anjali Verma","Karan Mehta","Sunita Devi","Ravi Kumar",
  "Aisha Khan","Ayesha Tariq","Bilal Ahmad","Farhan Malik","Hina Raza",
  "Kashif Iqbal","Zainab Fatima","Usman Sheikh","Sanaullah","Mariam Aslam",
  "Olamide Adekunle","Chinedu Okafor","Adaeze Eze","Tunde Bakare","Funke Ojo",
  "Emeka Onyeka","Folake Ade","Ngozi Nwosu","Ibrahim Bello","Yetunde Cole",
  "Suresh Yadav","Manish Goyal","Pooja Agarwal","Deepak Joshi","Neha Kapoor",
  "Hassan Raza","Tahir Mahmood","Nida Khan","Sajid Ali","Rabia Iqbal",
  "Olusegun Bello","Chiamaka Obi","Damilola Adigun","Babatunde Okeke","Aisha Garba",
  "Pranay Bhatt","Tanmay Saha","Riya Bansal","Yash Choudhary","Anu Rai",
  "Mubashir Rana","Saima Bukhari","Asif Latif","Sumaira Anwar","Mehdi Hassan",
  "Olufemi Adesina","Ifeoma Okoye","Tope Williams","Esther Akinola","Bayo Adeyemi",
  "Vivek Sinha","Shreya Das","Manoj Tiwari","Kavya Menon","Aniket Banerjee",
  "Imtiaz Khattak","Shumaila Sheikh","Adeel Younis","Bushra Khan","Faraz Akram",
  "Olamide Sanni","Kemi Adeola","Uchechi Eze","Bola Olatunji","Tobi Adeyinka",
  "Kiran Bedi","Sahil Garg","Aastha Chawla","Lakshya Negi","Megha Pillai",
  "Dilshad Hussain","Romaisa Tariq","Junaid Aslam","Hira Pervaiz","Mehwish Anwar",
  "Funmi Idowu","Segun Lawal","Chinasa Ezeh","Wale Olatunde","Adekemi Oyebanji",
];

const COUNTRIES = ["India","Pakistan","Nigeria","Bangladesh","Indonesia","UAE","Egypt"];
const COUNTRY_FLAG = { India: "🇮🇳", Pakistan: "🇵🇰", Nigeria: "🇳🇬", Bangladesh: "🇧🇩", Indonesia: "🇮🇩", UAE: "🇦🇪", Egypt: "🇪🇬" };
const TX_TYPES = ["Withdrawal","Deposit","Stake","Unstake","P2P Buy","P2P Sell","Mining Reward","Spin Win","Referral Bonus"];

const rand = (a, b) => a + Math.random() * (b - a);
const choice = (arr) => arr[Math.floor(Math.random() * arr.length)];

// =================== LOGIN GATE ================================================
const LoginGate = ({ onSuccess }) => {
  const [u, setU] = useState("");
  const [p, setP] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [err, setErr] = useState("");

  const submit = (e) => {
    e?.preventDefault?.();
    const userClean = (u || "").trim().toLowerCase();
    const passClean = (p || "").trim();
    if (!userClean || !passClean) {
      setErr("Username aur password dono fill karo");
      return;
    }
    if (userClean === SHOW_USER.toLowerCase() && passClean === SHOW_PASS) {
      localStorage.setItem(STORAGE_KEY, "1");
      onSuccess();
    } else {
      setErr(
        userClean !== SHOW_USER.toLowerCase()
          ? "Username galat hai (use: admin)"
          : "Password galat hai — case + special characters check karo"
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-indigo-950 to-slate-900 flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-slate-900/80 backdrop-blur-xl border border-indigo-500/30 rounded-2xl p-7 space-y-5 shadow-2xl" data-testid="showcase-login-form">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg">
            <ShieldCheck className="text-white" size={26} />
          </div>
          <h1 className="text-xl font-bold text-white tracking-tight">Console Access</h1>
          <p className="text-xs text-slate-400">Authorized personnel only</p>
        </div>
        <div className="space-y-3">
          <input value={u} onChange={(e) => setU(e.target.value)} placeholder="Username (admin)" autoFocus
            autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck="false"
            className="w-full bg-slate-800/70 border border-slate-700 focus:border-indigo-500 outline-none rounded-lg px-3 py-2.5 text-sm text-white" data-testid="showcase-user-input" />
          <div className="relative">
            <input value={p} onChange={(e) => setP(e.target.value)} placeholder="Password" type={showPw ? "text" : "password"}
              autoComplete="off" autoCapitalize="off" autoCorrect="off" spellCheck="false"
              className="w-full bg-slate-800/70 border border-slate-700 focus:border-indigo-500 outline-none rounded-lg px-3 py-2.5 pr-10 text-sm text-white" data-testid="showcase-pass-input" />
            <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </div>
        {err && <div className="text-xs text-red-400 text-center" data-testid="showcase-login-error">{err}</div>}
        <button type="submit" className="w-full py-2.5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-lg text-sm font-semibold transition" data-testid="showcase-login-submit">
          Sign In
        </button>
      </form>
    </div>
  );
};

// =================== MAIN SHOWCASE PAGE ========================================
const ShowcaseAdminPage = () => {
  const [authed, setAuthed] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [snapshot, setSnapshot] = useState([]);
  const [search, setSearch] = useState("");
  const [revealedRows, setRevealedRows] = useState({}); // {id: true}
  const [checked, setChecked] = useState({}); // {id: true}
  const [processing, setProcessing] = useState({}); // {id: timestamp}
  const [completed, setCompleted] = useState({}); // {id: true}
  const [now, setNow] = useState(Date.now());
  const [totalUsers, setTotalUsers] = useState(2_345_738);
  const [companyWallet, setCompanyWallet] = useState(8_412_337.42);
  const [dailyProfitPct, setDailyProfitPct] = useState(4.0);
  const completedQueueRef = useRef([]); // ids to auto-complete in 2-min cycles

  // Fetch real snapshot (read-only)
  useEffect(() => {
    if (!authed) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/showcase/withdrawal-snapshot?limit=20`);
        setSnapshot(r.data?.records || []);
      } catch (_) {
        setSnapshot([]);
      }
    })();
  }, [authed]);

  // === Build 600 transactions (mix real snapshot names with synthetic) =========
  const transactions = useMemo(() => {
    const list = [];
    const nowMs = Date.now();
    // Insert real snapshot entries first (so they appear near top)
    snapshot.forEach((r, i) => {
      list.push({
        id: `r_${i}_${(r.name || "user").replace(/\s+/g, "_")}`,
        name: r.name,
        type: "Withdrawal",
        amount: r.amount,
        currency: r.currency || "USDT",
        country: choice(["India", "Pakistan", "Nigeria"]),
        created_at: r.created_at || new Date(nowMs - Math.random() * 8 * 3600_000).toISOString(),
      });
    });
    // Pad to 600 with synthetic
    while (list.length < 612) {
      const name = choice(NAME_POOL);
      const type = "Withdrawal";
      const amount = +rand(20, 4500).toFixed(2);
      list.push({
        id: `s_${list.length}_${name.replace(/\s+/g, "_")}`,
        name,
        type,
        amount,
        currency: "USDT",
        country: choice(COUNTRIES),
        created_at: new Date(nowMs - Math.random() * 12 * 3600_000).toISOString(),
      });
    }
    // Newest first
    return list.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [snapshot]);

  // Build initial processing queue order (older items go first)
  useEffect(() => {
    if (transactions.length && completedQueueRef.current.length === 0) {
      completedQueueRef.current = [...transactions].reverse().map((t) => t.id);
    }
  }, [transactions]);

  // Tick every second (for countdown UI + auto-complete cycle)
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  // Auto-complete: every ~3 minutes, mark one queued tx as completed
  useEffect(() => {
    const iv = setInterval(() => {
      // pop the next id and complete it (if not already complete)
      while (completedQueueRef.current.length) {
        const id = completedQueueRef.current.shift();
        if (!completed[id]) {
          setCompleted((prev) => ({ ...prev, [id]: Date.now() }));
          setProcessing((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
          });
          break;
        }
      }
    }, 180_000);
    return () => clearInterval(iv);
  }, [completed]);

  // Slowly increment total users
  useEffect(() => {
    const iv = setInterval(() => {
      setTotalUsers((t) => t + (1 + Math.floor(Math.random() * 5))); // +1..+5 every 30s
    }, 30_000);
    return () => clearInterval(iv);
  }, []);

  // Drift company wallet up/down slowly
  useEffect(() => {
    const iv = setInterval(() => {
      setCompanyWallet((w) => Math.max(8_000_000, w + (Math.random() - 0.45) * 2400));
    }, 8000);
    return () => clearInterval(iv);
  }, []);

  // Change daily profit % every minute (2–6% range)
  useEffect(() => {
    const iv = setInterval(() => {
      setDailyProfitPct(+(2 + Math.random() * 4).toFixed(1));
    }, 60_000);
    return () => clearInterval(iv);
  }, []);

  const toggleCheck = (id) => setChecked((p) => ({ ...p, [id]: !p[id] }));
  const toggleReveal = (id) => setRevealedRows((p) => ({ ...p, [id]: !p[id] }));

  // Generate a 7-day profit trend bar chart (must be declared BEFORE any early return)
  const trend = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 7; i++) arr.push(+(2 + Math.random() * 5).toFixed(1));
    return arr;
  }, []);

  const pushToProcessing = () => {
    const ids = Object.keys(checked).filter((id) => checked[id] && !processing[id] && !completed[id]);
    if (!ids.length) return;
    const stamp = Date.now();
    setProcessing((prev) => {
      const next = { ...prev };
      ids.forEach((id) => { next[id] = stamp; });
      return next;
    });
    setChecked({});
    // Front-load these in the auto-complete queue
    completedQueueRef.current = [...ids, ...completedQueueRef.current.filter((q) => !ids.includes(q))];
  };

  // 3-min countdown remaining for each processing item
  const remainingSec = (startedAt) => {
    const elapsed = Math.floor((now - startedAt) / 1000);
    return Math.max(0, 180 - elapsed);
  };

  const filteredTx = useMemo(() => {
    if (!search.trim()) return transactions;
    const q = search.trim().toLowerCase();
    return transactions.filter((t) =>
      t.name.toLowerCase().includes(q) ||
      t.type.toLowerCase().includes(q) ||
      String(t.amount).includes(q)
    );
  }, [transactions, search]);

  const logout = () => {
    localStorage.removeItem(STORAGE_KEY);
    setAuthed(false);
  };

  if (!authed) return <LoginGate onSuccess={() => setAuthed(true)} />;

  const checkedCount = Object.values(checked).filter(Boolean).length;
  const processingCount = Object.keys(processing).length;
  const completedToday = Object.keys(completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-indigo-950 text-white" data-testid="showcase-admin-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
              <ShieldCheck size={18} className="text-white" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-tight">Operations Console</div>
              <div className="text-[10px] text-slate-400">Logged in as <span className="text-indigo-300">admin</span></div>
            </div>
          </div>
          <button onClick={logout} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 rounded-lg text-xs flex items-center gap-1.5" data-testid="showcase-logout">
            <LogOut size={13} /> Logout
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-5 space-y-5">
        {/* KPI grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard
            icon={<Users size={18} className="text-blue-400" />}
            label="Total Users"
            value={totalUsers.toLocaleString()}
            hint="+247 in last 24h"
            color="from-blue-500/15 to-blue-500/5"
            testId="kpi-total-users"
          />
          <KpiCard
            icon={<Wallet size={18} className="text-emerald-400" />}
            label="Company Wallet"
            value={`$${companyWallet.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
            hint="Treasury holdings"
            color="from-emerald-500/15 to-emerald-500/5"
            testId="kpi-company-wallet"
          />
          <KpiCard
            icon={<TrendingUp size={18} className="text-amber-400" />}
            label="Today's Profit"
            value={`${dailyProfitPct}%`}
            hint={`Avg 7d: ${(trend.reduce((a, b) => a + b, 0) / trend.length).toFixed(2)}%`}
            color="from-amber-500/15 to-amber-500/5"
            testId="kpi-daily-profit"
          />
          <KpiCard
            icon={<Activity size={18} className="text-purple-400" />}
            label="Active Sessions"
            value={(1820 + Math.floor((now / 1000) % 240)).toLocaleString()}
            hint="Live now"
            color="from-purple-500/15 to-purple-500/5"
            testId="kpi-active-sessions"
          />
        </div>

        {/* Profit trend bar chart */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-semibold flex items-center gap-2">
              <TrendingUp size={15} className="text-emerald-400" /> Daily Profit (Last 7 days)
            </div>
            <div className="text-xs text-slate-400">Rolling treasury yield</div>
          </div>
          <div className="flex items-end gap-2 h-32">
            {trend.map((pct, i) => {
              const h = (pct / 7) * 100;
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1 group">
                  <div className="text-[10px] text-slate-300 opacity-0 group-hover:opacity-100 transition">{pct}%</div>
                  <div className="w-full rounded-t bg-gradient-to-t from-emerald-600 to-emerald-300 transition hover:from-emerald-400" style={{ height: `${h}%` }} />
                  <div className="text-[9px] text-slate-500">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i]}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Action bar */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <input
              placeholder="Search by name, type, or amount…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-slate-800/70 border border-slate-700 outline-none rounded-lg pl-9 pr-3 py-2 text-sm"
              data-testid="showcase-search"
            />
          </div>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-slate-400">Selected: <b className="text-white">{checkedCount}</b></span>
            <span className="text-slate-400">Processing: <b className="text-amber-300">{processingCount}</b></span>
            <span className="text-slate-400">Completed: <b className="text-emerald-300">{completedToday}</b></span>
          </div>
          <button
            onClick={pushToProcessing}
            disabled={checkedCount === 0}
            className={`px-4 py-2 rounded-lg text-xs font-semibold transition ${checkedCount === 0 ? "bg-slate-800 text-slate-500 cursor-not-allowed" : "bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white"}`}
            data-testid="showcase-push-processing"
          >
            Push to Withdrawal ({checkedCount})
          </button>
        </div>

        {/* Transactions table */}
        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
            <div className="text-sm font-semibold">Transactions <span className="text-slate-500 text-xs">({filteredTx.length})</span></div>
            <div className="text-[11px] text-slate-400">Tap a row to reveal real name</div>
          </div>
          <div className="overflow-x-auto max-h-[640px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-900 sticky top-0 z-10">
                <tr className="text-slate-400 text-left">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-2 py-2">Account</th>
                  <th className="px-2 py-2">Type</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {filteredTx.map((t) => {
                  const code = codenameFromString(t.id);
                  const isRevealed = !!revealedRows[t.id];
                  const isProcessing = !!processing[t.id];
                  const isCompleted = !!completed[t.id];
                  const rem = isProcessing ? remainingSec(processing[t.id]) : 0;
                  return (
                    <tr
                      key={t.id}
                      className={`border-t border-slate-800/60 hover:bg-slate-800/40 transition ${isCompleted ? "opacity-50" : ""}`}
                      data-testid={`showcase-tx-row-${t.id}`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          disabled={isCompleted || isProcessing}
                          checked={!!checked[t.id]}
                          onChange={() => toggleCheck(t.id)}
                          className="w-4 h-4 accent-indigo-500"
                          data-testid={`showcase-tx-check-${t.id}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <button
                          onClick={() => toggleReveal(t.id)}
                          className="text-left group"
                          data-testid={`showcase-reveal-${t.id}`}
                        >
                          {isRevealed ? (
                            <span className="text-white font-medium">{t.name}</span>
                          ) : (
                            <span className="font-mono text-indigo-300 group-hover:text-indigo-200 underline-offset-2 group-hover:underline">{code}</span>
                          )}
                          <span className="block text-[10px] text-slate-500">{isRevealed ? "Tap to hide" : "Tap to reveal"}</span>
                        </button>
                      </td>
                      <td className="px-2 py-2">
                        <span className="px-1.5 py-0.5 rounded bg-slate-800 text-[10px]">{t.type}</span>
                      </td>
                      <td className="px-2 py-2 text-right font-mono">
                        {t.currency === "USDT" ? "$" : ""}{t.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {t.currency !== "USDT" && <span className="text-[9px] text-slate-500 ml-1">{t.currency}</span>}
                      </td>
                      <td className="px-2 py-2">
                        {isCompleted ? (
                          <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 size={11} /> Completed</span>
                        ) : isProcessing ? (
                          <span className="text-amber-300 flex items-center gap-1">
                            <Clock size={11} className="animate-pulse" /> Under Processing
                            <span className="ml-1 text-[10px] text-slate-400 font-mono">{Math.floor(rem / 60)}:{String(rem % 60).padStart(2, "0")}</span>
                          </span>
                        ) : (
                          <span className="text-slate-400">Pending</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-slate-500 text-[10px] whitespace-nowrap">
                        {timeAgo(t.created_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, hint, color, testId }) => (
  <div className={`bg-gradient-to-br ${color} border border-slate-800 rounded-xl p-4`} data-testid={testId}>
    <div className="flex items-center gap-2 text-xs text-slate-300 mb-1.5">
      {icon}
      <span>{label}</span>
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
    {hint && <div className="text-[10px] text-slate-400 mt-1">{hint}</div>}
  </div>
);

const timeAgo = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const sec = Math.floor((Date.now() - d.getTime()) / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

export default ShowcaseAdminPage;
